import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ArrowRight, Building2, Check, LogOut } from 'lucide-react';
import { useAuthContext } from './components/AuthProvider';
import OrganizationIcon from './components/OrganizationIcon';
import { supabase } from './lib/supabaseClient';
import { acceptOrgInvite, acceptOrgInvitePublic, validateOrgInvite } from './lib/api';
import './InviteAcceptPage.css';

const PENDING_INVITE_KEY = 'latted_pending_org_invite';

function invitedUserName(user) {
  return user?.user_metadata?.full_name || user?.user_metadata?.name || '';
}

function formatExpiry(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  }).format(date);
}

function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuthContext();

  const inviteToken = useMemo(() => {
    const queryToken = searchParams.get('invite') || searchParams.get('token') || '';
    if (queryToken) {
      try { sessionStorage.setItem(PENDING_INVITE_KEY, queryToken); } catch { /* ignore */ }
      return queryToken;
    }
    try { return sessionStorage.getItem(PENDING_INVITE_KEY) || ''; } catch { return ''; }
  }, [searchParams]);

  const [invite, setInvite] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken));
  const [inviteError, setInviteError] = useState(null);
  const [mode, setMode] = useState('create');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [transitionOverride, setTransitionOverride] = useState(null);
  const [successOrg, setSuccessOrg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!inviteToken) {
      setInviteLoading(false);
      setInviteError({ code: 'INVITE_TOKEN_REQUIRED', message: 'This invitation link is missing its token.' });
      return undefined;
    }

    setInviteLoading(true);
    setInviteError(null);
    validateOrgInvite(inviteToken)
      .then((result) => {
        if (!cancelled) setInvite(result);
      })
      .catch((error) => {
        if (!cancelled) {
          if (error.status >= 400 && error.status < 500) {
            try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
          }
          setInviteError({
            code: error.code || 'INVITE_INVALID',
            message: 'This invitation could not be verified.',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setInviteLoading(false);
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const invitedEmail = invite?.email || '';
  const signedInEmail = user?.email || '';
  const emailMatches = Boolean(
    invitedEmail && signedInEmail && invitedEmail.toLowerCase() === signedInEmail.toLowerCase()
  );
  const expiry = formatExpiry(invite?.expiresAt);
  const membershipTransition = transitionOverride || invite?.membershipTransition || null;
  const currentOrganization = membershipTransition?.currentOrganization || null;
  const currentOrganizationName = currentOrganization?.name || 'your current organization';
  const switchStrategy = membershipTransition?.strategy || '';
  const switchBlocked = membershipTransition?.canAccept === false;

  let switchTitle = `Leave ${currentOrganizationName} and join ${invite?.organizationName}?`;
  let switchDescription = `You will lose access to ${currentOrganizationName}'s shared projects and assets. That content stays with the organization.`;
  let switchActionLabel = `Leave and join ${invite?.organizationName}`;
  if (switchStrategy === 'personal_workspace_transfer') {
    switchTitle = `Move your workspace to ${invite?.organizationName}?`;
    switchDescription = `Your projects and workspace assets will move from ${currentOrganizationName} into ${invite?.organizationName}. The old personal organization will be removed.`;
    switchActionLabel = 'Move workspace and join';
  } else if (switchStrategy === 'owner_transfer_required') {
    switchTitle = `Transfer ownership of ${currentOrganizationName} first`;
    switchDescription = `You own ${currentOrganizationName}${currentOrganization?.memberCount > 1 ? `, which has ${currentOrganization.memberCount} members` : ''}. Transfer ownership or remove its other members before accepting this invitation.`;
  }

  const complete = (organization) => {
    try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
    setSuccessOrg(organization || { name: invite?.organizationName });
    setActionError('');
    setTransitionOverride(null);
  };

  const showRequiredTransition = async (error) => {
    const blocked = error.code === 'ORGANIZATION_OWNER_TRANSFER_REQUIRED';
    const personal = error.code === 'PERSONAL_ORG_TRANSFER_REQUIRED';
    try {
      const refreshedInvite = await validateOrgInvite(inviteToken);
      setInvite(refreshedInvite);
      if (refreshedInvite?.membershipTransition) {
        setTransitionOverride(null);
        return;
      }
    } catch {
      // Preserve the valid invitation already on screen and use safe fallback copy.
    }
    setTransitionOverride({
      required: true,
      canAccept: !blocked,
      strategy: blocked
        ? 'owner_transfer_required'
        : (personal ? 'personal_workspace_transfer' : 'leave_current_organization'),
      currentOrganization: {
        name: 'your current organization',
        role: blocked ? 'owner' : 'user',
      },
    });
  };

  const acceptForSignedInUser = async (confirmOrganizationSwitch = false) => {
    setSubmitting(true);
    setActionError('');
    try {
      const result = await acceptOrgInvite({
        inviteToken,
        displayName: invitedUserName(user) || displayName.trim() || undefined,
        confirmOrganizationSwitch,
        transferPersonalOrganization: (
          confirmOrganizationSwitch && switchStrategy === 'personal_workspace_transfer'
        ),
      });
      complete(result?.organization);
    } catch (error) {
      if ([
        'ORGANIZATION_SWITCH_REQUIRED',
        'ORGANIZATION_OWNER_TRANSFER_REQUIRED',
        'PERSONAL_ORG_TRANSFER_REQUIRED',
      ].includes(error.code)) {
        await showRequiredTransition(error);
      } else {
        setActionError('The invitation could not be accepted.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAccount = async (event) => {
    event.preventDefault();
    setActionError('');
    if (!displayName.trim()) {
      setActionError('Enter your name.');
      return;
    }
    if (password.length < 8) {
      setActionError('Use at least 8 characters for your password.');
      return;
    }
    if (password !== confirmPassword) {
      setActionError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await acceptOrgInvitePublic({
        inviteToken,
        displayName: displayName.trim(),
        password,
      });
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitedEmail,
        password,
      });
      if (signInError) {
        setMode('signin');
        setActionError('Your account joined the organization. Sign in to continue.');
        return;
      }
      complete(result?.organization);
    } catch (error) {
      if (error.code === 'USER_EXISTS') {
        setMode('signin');
        setActionError('An account already exists for this email. Sign in to accept the invitation.');
      } else {
        setActionError('We could not create your account.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignIn = async (event) => {
    event.preventDefault();
    setActionError('');
    if (!password) {
      setActionError('Enter your password.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: invitedEmail,
        password,
      });
      if (signInError) throw signInError;

      const refreshedInvite = await validateOrgInvite(inviteToken);
      setInvite(refreshedInvite);
      if (refreshedInvite?.membershipTransition?.required) {
        setTransitionOverride(null);
        return;
      }

      const result = await acceptOrgInvite({
        inviteToken,
        displayName: invitedUserName(data?.user) || undefined,
        confirmOrganizationSwitch: false,
        transferPersonalOrganization: false,
      });
      complete(result?.organization);
    } catch (error) {
      if ([
        'ORGANIZATION_SWITCH_REQUIRED',
        'ORGANIZATION_OWNER_TRANSFER_REQUIRED',
        'PERSONAL_ORG_TRANSFER_REQUIRED',
      ].includes(error.code)) {
        await showRequiredTransition(error);
      } else {
        setActionError('Sign-in failed. Check your password and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setActionError('');
    setSubmitting(true);
    try {
      sessionStorage.setItem(PENDING_INVITE_KEY, inviteToken);
      const redirectTo = `${window.location.origin}/invite/accept?invite=${encodeURIComponent(inviteToken)}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo },
      });
      if (error) throw error;
    } catch (error) {
      setActionError('Google sign-in could not be started.');
      setSubmitting(false);
    }
  };

  const switchAccount = async () => {
    setActionError('');
    await signOut();
  };

  const stayInCurrentOrganization = () => {
    try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
    navigate(switchBlocked ? '/account#organization' : '/account');
  };

  if (successOrg) {
    return (
      <main className="invite-accept-page">
        <section className="invite-accept-card invite-accept-result" aria-live="polite">
          <div className="invite-accept-status-icon invite-accept-status-icon--success"><Check size={22} /></div>
          <p className="invite-accept-eyebrow">Invitation accepted</p>
          <h1>You're in {successOrg.name || invite?.organizationName}</h1>
          <p>You now have access to the organization's shared resources.</p>
          <button type="button" className="invite-accept-primary" onClick={() => navigate('/account')}>
            Open Latted <ArrowRight size={16} />
          </button>
        </section>
      </main>
    );
  }

  if (inviteLoading || authLoading) {
    return (
      <main className="invite-accept-page">
        <section className="invite-accept-card invite-accept-result" aria-live="polite">
          <div className="invite-accept-spinner" aria-hidden="true" />
          <h1>Checking invitation</h1>
          <p>Confirming the organization and account details…</p>
        </section>
      </main>
    );
  }

  if (inviteError || !invite) {
    return (
      <main className="invite-accept-page">
        <section className="invite-accept-card invite-accept-result" role="alert">
          <div className="invite-accept-status-icon invite-accept-status-icon--error"><AlertCircle size={22} /></div>
          <p className="invite-accept-eyebrow">Invitation unavailable</p>
          <h1>This link can't be used</h1>
          <p>{'Ask the organization owner to send a new invitation.'}</p>
          <button type="button" className="invite-accept-secondary" onClick={() => {
            try { sessionStorage.removeItem(PENDING_INVITE_KEY); } catch { /* ignore */ }
            navigate('/');
          }}>
            Return to Latted
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="invite-accept-page">
      <section className="invite-accept-card" aria-labelledby="invite-title">
        <header className="invite-accept-header">
          <div className="invite-accept-brand"><Building2 size={17} /> Latted organization</div>
          <p className="invite-accept-eyebrow">You've been invited</p>
          <h1 id="invite-title">Join {invite.organizationName}</h1>
          <p className="invite-accept-subtitle">
            Collaborate on shared projects, assets, characters, voices, spaces, and workflows.
          </p>
        </header>

        <dl className="invite-accept-summary">
          <div>
            <dt>Organization</dt>
            <dd className="invite-accept-org-name">
              <OrganizationIcon
                className="invite-accept-org-icon"
                iconUrl={invite.organizationIconUrl}
                name={invite.organizationName}
              />
              <span>{invite.organizationName}</span>
            </dd>
          </div>
          <div><dt>Invited email</dt><dd>{invitedEmail}</dd></div>
          {expiry && <div><dt>Expires</dt><dd>{expiry}</dd></div>}
        </dl>

        {user ? (
          <div className="invite-accept-authenticated">
            <div className={`invite-accept-account ${emailMatches ? '' : 'invite-accept-account--mismatch'}`}>
              <div>
                <span>Signed in as</span>
                <strong>{signedInEmail}</strong>
              </div>
              {!emailMatches && <AlertCircle size={17} />}
            </div>

            {emailMatches ? (
              <>
                {membershipTransition?.required && (
                  <div className={`invite-accept-transition ${switchBlocked ? 'invite-accept-transition--blocked' : ''}`} role="alert">
                    <div className="invite-accept-transition-heading">
                      <AlertTriangle size={17} aria-hidden="true" />
                      <div>
                        <span>Organization change</span>
                        <strong>{switchTitle}</strong>
                      </div>
                    </div>
                    <div className="invite-accept-transition-route" aria-label={`Organization change from ${currentOrganizationName} to ${invite.organizationName}`}>
                      <div className="invite-accept-transition-org">
                        <OrganizationIcon
                          className="invite-accept-org-icon"
                          iconUrl={currentOrganization?.iconUrl}
                          name={currentOrganizationName}
                        />
                        <div>
                          <small>Current</small>
                          <strong>{currentOrganizationName}</strong>
                          <span>{currentOrganization?.role === 'owner' ? 'Owner' : 'Member'}</span>
                        </div>
                      </div>
                      <ArrowRight size={16} aria-hidden="true" />
                      <div className="invite-accept-transition-org">
                        <OrganizationIcon
                          className="invite-accept-org-icon"
                          iconUrl={invite.organizationIconUrl}
                          name={invite.organizationName}
                        />
                        <div>
                          <small>New</small>
                          <strong>{invite.organizationName}</strong>
                          <span>Member</span>
                        </div>
                      </div>
                    </div>
                    <p>{switchDescription}</p>
                    <div className="invite-accept-transition-actions">
                      <button type="button" className="invite-accept-secondary" onClick={stayInCurrentOrganization} disabled={submitting}>
                        {switchBlocked ? 'Open organization settings' : `Stay in ${currentOrganizationName}`}
                      </button>
                      {!switchBlocked && (
                        <button type="button" className="invite-accept-primary" onClick={() => acceptForSignedInUser(true)} disabled={submitting}>
                          {submitting ? 'Switching organization…' : switchActionLabel}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {!membershipTransition?.required && (
                  <button type="button" className="invite-accept-primary" onClick={() => acceptForSignedInUser(false)} disabled={submitting}>
                    {submitting ? 'Joining…' : `Join ${invite.organizationName}`} <ArrowRight size={16} />
                  </button>
                )}
              </>
            ) : (
              <div className="invite-accept-mismatch">
                <p>This invitation belongs to <strong>{invitedEmail}</strong>. Switch accounts to continue.</p>
                <button type="button" className="invite-accept-secondary" onClick={switchAccount}>
                  <LogOut size={15} /> Switch account
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="invite-accept-guest">
            <div className="invite-accept-tabs" role="tablist" aria-label="Account options">
              <button type="button" role="tab" aria-selected={mode === 'create'} disabled={submitting} onClick={() => { setMode('create'); setActionError(''); }}>
                Create account
              </button>
              <button type="button" role="tab" aria-selected={mode === 'signin'} disabled={submitting} onClick={() => { setMode('signin'); setActionError(''); }}>
                Sign in
              </button>
            </div>

            <button type="button" className="invite-accept-google" onClick={handleGoogleSignIn} disabled={submitting}>
              <img src="/google.svg" alt="" /> Continue with Google
            </button>
            <div className="invite-accept-divider"><span>or use email</span></div>

            {mode === 'create' ? (
              <form className="invite-accept-form" onSubmit={handleCreateAccount}>
                <label>
                  Full name
                  <input autoFocus type="text" autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Your name" />
                </label>
                <label>
                  Email
                  <input type="email" autoComplete="email" value={invitedEmail} readOnly />
                </label>
                <div className="invite-accept-password-row">
                  <label>
                    Password
                    <input type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="At least 8 characters" />
                  </label>
                  <label>
                    Confirm
                    <input type="password" autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Repeat password" />
                  </label>
                </div>
                <button type="submit" className="invite-accept-primary" disabled={submitting}>
                  {submitting ? 'Creating account…' : 'Create account and join'} <ArrowRight size={16} />
                </button>
              </form>
            ) : (
              <form className="invite-accept-form" onSubmit={handleSignIn}>
                <label>
                  Email
                  <input type="email" autoComplete="email" value={invitedEmail} readOnly />
                </label>
                <label>
                  Password
                  <input autoFocus type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Your password" />
                </label>
                <button type="submit" className="invite-accept-primary" disabled={submitting}>
                  {submitting ? 'Signing in…' : 'Sign in and join'} <ArrowRight size={16} />
                </button>
              </form>
            )}
          </div>
        )}

        {actionError && <div className="invite-accept-error" role="alert"><AlertCircle size={15} /> {actionError}</div>}
        <p className="invite-accept-footnote">By continuing, you agree to Latted's Terms of Service and Privacy Policy.</p>
      </section>
    </main>
  );
}

export default InviteAcceptPage;
